<template>
  <div class="chat-video-player">
    <!-- Chat-only uploading UI -->
    <div
      v-if="status === 'UPLOADING' && localPreviewUrl"
      class="chat-video-thumb chat-video-thumb--uploading"
      :style="{ width: width, ...(height && height !== 'auto' ? { height: height } : {}) }"
    >
      <video
        class="chat-video-preview"
        :src="localPreviewUrl"
        muted
        playsinline
        preload="metadata"
      />
      <div class="chat-video-progress">
        <a-progress
          :percent="Math.min(100, Math.round(uploadProgress))"
          :status="uploadProgress >= 100 ? 'success' : 'active'"
        />
      </div>
    </div>
    <div
      v-else-if="status === 'UPLOADING'"
      class="chat-video-uploading-fallback"
      :style="{ width: width, ...(height && height !== 'auto' ? { height: height } : {}) }"
    >
      <a-progress
        :percent="Math.min(100, Math.round(uploadProgress))"
        status="active"
      />
    </div>

    <!-- Chat-only: if thumbnailUrl is actually a video/blob URL, render it as a <video> preview -->
    <div
      v-else-if="!isPlaying && thumbnailVideoSrc"
      class="chat-video-thumb"
      :style="{ width: width, ...(height && height !== 'auto' ? { height: height } : {}) }"
      @click="onPlay"
    >
      <video
        class="chat-video-preview"
        :src="thumbnailVideoSrc"
        muted
        playsinline
        preload="metadata"
      />
      <div class="chat-video-play-overlay">
        <a-icon type="play-circle" :style="{ fontSize: '64px', color: '#fff' }" />
      </div>
    </div>

    <!-- Default shared player for PROCESSING/READY/FAILED -->
    <VideoPlayer
      v-else
      ref="player"
      class="chat-video-shared"
      :hls-url="hlsUrl"
      :thumbnail-url="thumbnailUrl"
      :status="normalizedStatus"
      :resources-url="resourcesUrl"
      :original-key="originalKey"
      :renditions="renditions"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script>
import VideoPlayer from "@/components/VideoPlayer.vue";

export default {
  name: "ChatVideoPlayer",
  components: { VideoPlayer },
  props: {
    hlsUrl: { type: String, default: null },
    thumbnailUrl: { type: String, default: null },
    resourcesUrl: { type: String, required: true },
    originalKey: { type: String, default: null },
    renditions: { type: Array, default: () => [] },
    width: { type: String, default: "100%" },
    height: { type: String, default: "auto" },
    status: { type: String, default: "PROCESSING" }, // UPLOADING | PROCESSING | READY | FAILED
    uploadProgress: { type: Number, default: 0 },
    localPreviewUrl: { type: String, default: null },
  },
  data() {
    return { isPlaying: false };
  },
  computed: {
    normalizedStatus() {
      // Shared VideoPlayer doesn't know about UPLOADING
      return this.status === "UPLOADING" ? "PROCESSING" : this.status;
    },
    thumbnailVideoSrc() {
      if (this.status === "UPLOADING") return null;
      if (!this.thumbnailUrl) return null;
      // If it's NOT an image, we treat it as a video/blob URL for chat preview
      const isImage = /\.(jpe?g|png|gif|webp)(\?|#|$)/i.test(this.thumbnailUrl);
      return isImage ? null : this.thumbnailUrl;
    },
  },
  methods: {
    onPlay() {
      // Forward to shared player play if present; otherwise just mark as playing so we swap rendering
      this.isPlaying = true;
      this.$nextTick(() => {
        const player = this.$refs.player;
        if (player && typeof player.playVideo === "function") {
          player.playVideo();
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
.chat-video-thumb {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: #000;
  cursor: pointer;
  aspect-ratio: 16 / 9;
}

.chat-video-thumb--uploading {
  cursor: default;
}

.chat-video-preview {
  width: 100%;
  height: auto;
  max-height: 360px;
  object-fit: contain;
  display: block;
}

.chat-video-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.28);
}

.chat-video-progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px 12px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.82));
}

.chat-video-progress ::v-deep .ant-progress-bg {
  height: 6px !important;
}

.chat-video-uploading-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  background: #1a1a1a;
  border-radius: 8px;
  box-sizing: border-box;
}
</style>

